import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado } from '../../componentes/estados';
import { CampoData, Rotulo, useCamposPersonalizados, validarCampos, valorExtra, EntradaSensivel } from '../../componentes/formulario';
import { AvisoConflitosLivre, CamposHorarioLivre, diasDoDiaInteiro, estadoLivreDaReuniao, horarioLivreParaApi, temErroForaDaTela, validarReuniaoLivre, type EstadoReuniaoLivre } from '../../componentes/reuniao-livre';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, ErroRequisicao, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data as fmtData, dataHora, dinheiro, hora, nomeCadastro, nomeParceiro, telefoneFormatado, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { focoPerdido } from '../../nucleo/foco';
import { DialogoCriacao, useAbrirCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import { CHAVE, diaLocal, useNomeDoRecurso } from './dados';
import { CamposExtrasAgendamento, ENTIDADE_AGENDAMENTO } from './NovoAgendamento';
import { CampoConvidados, chaveConvidado, convidadosParaApi, rotuloConvidado } from './Convidados';
import { CampoReuniao, reuniaoParaApi } from './Reuniao';
import { SeletorHorario } from './SeletorHorario';
import { Gravacoes } from './Gravacoes';
import { acoesDaReuniao, COR_ESTADO, ehReuniaoLivre, ESTADOS_ABERTOS, type Agendamento, type Convidado, type TipoReuniao } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { LinkParceiro } from '../../componentes/link-parceiro';

/**
 * Review r4 (spec M7, baixa): campos personalizados do agendamento na ficha, com edição (PATCH /agenda/agendamentos/:id).
 * Os obrigatórios são conferidos aqui e no servidor; os erros voltam por campo.
 */
function ExtrasDoAgendamento({ a, podeEditar, aoSalvar }: { a: Agendamento; podeEditar: boolean; aoSalvar: () => Promise<void> }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const defs = useCamposPersonalizados(ENTIDADE_AGENDAMENTO);
  const [editando, setEditando] = useState(false);
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(erros, erro);
  const salvar = useMutation({
    mutationFn: () => patch(`/agenda/agendamentos/${a.id}`, { extras: valores }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      setEditando(false);
      await aoSalvar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });
  if (!defs.length) return null;
  const pendentes = defs.filter((d) => d.obrigatorio && valorExtra(d, a.extras?.[d.nome.slice(7)]) === '').length;
  function enviar() {
    const locais = validarCampos(defs, { extras: valores });
    setErros(locais);
    if (Object.keys(locais).length) return;
    salvar.mutate();
  }
  return (
    <section className="flex flex-col gap-3" aria-labelledby={`extras-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`extras-${a.id}`} className="kicker">
          {tn('campos.personalizados')}
        </h3>
        {podeEditar && !editando && (
          <Botao
            variante="fantasma"
            onClick={() => {
              setValores({ ...(a.extras ?? {}) });
              setErros({});
              setErro(null);
              setEditando(true);
            }}
          >
            {tn('comum.editar')}
          </Botao>
        )}
      </div>
      {!editando && pendentes > 0 && <p className="text-sm text-laranja">{t('detalhe.extrasPendentes', { count: pendentes })}</p>}
      {editando ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
              {erro}
            </p>
          )}
          <CamposExtrasAgendamento defs={defs} valores={valores} erros={erros} aoMudar={(k, v) => setValores((x) => ({ ...x, [k]: v }))} />
          <div className="flex flex-wrap gap-2 sm:col-span-6">
            <Botao variante="primario" carregando={salvar.isPending} onClick={enviar}>
              {tn('comum.salvar')}
            </Botao>
            <Botao variante="fantasma" onClick={() => setEditando(false)}>
              {tn('comum.cancelar')}
            </Botao>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          {defs.map((d) => (
            <div key={d.nome}>
              <dt className="kicker">{d.rotulo}</dt>
              <dd className="mt-1 break-words">{valorExtra(d, a.extras?.[d.nome.slice(7)], a.parceiro_nome) || tn('comum.naoInformado')}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Meet da reunião: consulta o link a cada 5 s, até 3 min depois de abrir (a sincronia devolve em segundos). */
const ESPERA_MEET_MS = 5_000;
const TETO_MEET_MS = 180_000;

/** Os campos que o bloco de remarcar mostra (`CamposHorarioLivre`). */
const CAMPOS_DO_REMARCAR = ['dia', 'dia_fim', 'inicio', 'fim'];

/**
 * Reunião livre (03/11): remarcar é escolher data, início e fim, sem a lista de horários livres de um serviço. Conflito só avisa.
 * Fica aqui, e não no "Editar reunião", porque remarcar pede `agenda.cancelar`, e editar, `agenda.editar`.
 */
function RemarcarReuniaoLivre({ a, aoRemarcar, aoFecharSemMudar }: { a: Agendamento; aoRemarcar: () => Promise<void>; aoFecharSemMudar: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [inicial] = useState<EstadoReuniaoLivre>(() => estadoLivreDaReuniao(a, s.workspace.fuso));
  const [estado, setEstado] = useState<EstadoReuniaoLivre>(inicial);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(erros, erro);
  const remarcar = useMutation({
    mutationFn: () => post(`/agenda/agendamentos/${a.id}/remarcar`, horarioLivreParaApi(estado)),
    onMutate: () => setErro(null),
    onSuccess: aoRemarcar,
    onError: (e) => {
      const porCampo = e instanceof ErroRequisicao ? e.porCampo() : {};
      setErros(porCampo);
      // Rodada rrl-6 (ROB6-M2): o erro de campo que este bloco não mostra (o profissional desativado) aparece em cima, com o motivo.
      if (temErroForaDaTela(porCampo, CAMPOS_DO_REMARCAR)) setErro(mensagemDeErro(e));
    },
  });
  const aplicar = () => {
    const locais = validarReuniaoLivre(estado, tn('erros.obrigatorio'), t);
    setErros(locais);
    if (Object.keys(locais).length) return;
    // Rodada rrl-6 (ROB6-B1): sem mudar o horário não há o que remarcar; a rota voltaria a confirmada para marcada, zeraria os lembretes e avisaria.
    if (JSON.stringify(horarioLivreParaApi(estado)) === JSON.stringify(horarioLivreParaApi(inicial))) aoFecharSemMudar();
    else remarcar.mutate();
  };
  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-xl border border-borda bg-sup-2 p-4" data-remarcar-livre="">
      <h3 className="text-sm font-semibold">{t('detalhe.remarcarTitulo')}</h3>
      {erro && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erro}
        </p>
      )}
      {/* Rodada rrl-2 (VIS-B3): num grupo só, porque a região vazia do aviso somaria um vão do flex; a margem é a do próprio aviso. */}
      <div className="min-w-0">
        <CamposHorarioLivre idBase={`rem-${a.id}`} estado={estado} aoMudar={setEstado} erros={erros} />
        <AvisoConflitosLivre estado={estado} ignorarId={a.id} ativo={a.responsavel_usuario_id === s.usuario.id} />
      </div>
      <div>
        <Botao carregando={remarcar.isPending} onClick={aplicar}>
          {t('acoes.aplicarRemarcacao')}
        </Botao>
      </div>
    </section>
  );
}

/** Link que abre em outra aba só quando é http ou https (o servidor já recusa o resto; dado antigo não vira link). */
function linkSeguro(v: string): string | null {
  try {
    const u = new URL(v);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

/** Lote 19 (12b): local, link da reunião e convidados na ficha, com edição (PATCH /agenda/agendamentos/:id). */
function DetalhesDoAgendamento({ a, podeEditar, aoSalvar, meetDemorou }: { a: Agendamento; podeEditar: boolean; aoSalvar: () => Promise<void>; meetDemorou?: boolean }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [editando, setEditando] = useState(false);
  const [local, setLocal] = useState('');
  const [link, setLink] = useState('');
  const [tipo, setTipo] = useState<TipoReuniao>('nenhuma');
  const [convidados, setConvidados] = useState<Convidado[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const salvar = useMutation({
    mutationFn: () => patch(`/agenda/agendamentos/${a.id}`, { local: local.trim() || null, ...reuniaoParaApi(tipo, link), convidados: convidadosParaApi(convidados) }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      setEditando(false);
      await aoSalvar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const href = a.link_reuniao ? linkSeguro(a.link_reuniao) : null;
  const vazio = !a.local && !a.link_reuniao && a.reuniao_tipo === 'nenhuma' && !a.convidados.length;
  return (
    <section className="flex flex-col gap-3" aria-labelledby={`detalhes-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`detalhes-${a.id}`} className="kicker">
          {t('detalhe.detalhesTitulo')}
        </h3>
        {podeEditar && !editando && (
          <Botao
            variante="fantasma"
            onClick={() => {
              setLocal(a.local ?? '');
              setLink(a.reuniao_tipo === 'link' ? (a.link_reuniao ?? '') : '');
              setTipo(a.reuniao_tipo);
              setConvidados(a.convidados);
              setErro(null);
              setEditando(true);
            }}
          >
            {tn('comum.editar')}
          </Botao>
        )}
      </div>
      {editando ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
              {erro}
            </p>
          )}
          <div className="sm:col-span-3">
            <Rotulo id={`det-local-${a.id}`} rotulo={t('campos.local')}>
              <EntradaSensivel id={`det-local-${a.id}`} mascara={null} livre maxLength={300} value={local} onChange={(e) => setLocal(e.target.value)} />
            </Rotulo>
          </div>
          {/* Lote 19 (12e): manter o tipo mantém o link (Meet e sala); trocar confere o conector ou o servidor. */}
          <CampoReuniao idBase={`det-reuniao-${a.id}`} recursoId={a.recurso_id} tipo={tipo} aoTipo={setTipo} link={link} aoLink={setLink} atual={a.reuniao_tipo} />
          <div className="sm:col-span-6">
            <CampoConvidados idBase={`det-conv-${a.id}`} valor={convidados} aoMudar={setConvidados} />
          </div>
          <div className="flex flex-wrap gap-2 sm:col-span-6">
            <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {tn('comum.salvar')}
            </Botao>
            <Botao variante="fantasma" onClick={() => setEditando(false)}>
              {tn('comum.cancelar')}
            </Botao>
          </div>
        </div>
      ) : vazio ? (
        <p className="text-sm text-tinta-3">{t('detalhe.semDetalhes')}</p>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          {a.local && (
            <div>
              <dt className="kicker">{t('campos.local')}</dt>
              <dd className="mt-1 break-words">{textoLivre(a.local, a.parceiro_nome, ...(a.convidados ?? []).map((c) => c.nome))}</dd>
            </div>
          )}
          {a.reuniao_tipo === 'google_meet' && !a.link_reuniao && ESTADOS_ABERTOS.includes(a.estado) && (
            <div data-meet-aguardando="">
              <dt className="kicker">{t('reuniao.google_meet')}</dt>
              <dd className="mt-1 text-tinta-3">{t(meetDemorou ? 'reuniao.meetDemorou' : 'reuniao.aguardandoMeet')}</dd>
            </div>
          )}
          {a.link_reuniao && (
            <div>
              <dt className="kicker">{a.reuniao_tipo === 'google_meet' || a.reuniao_tipo === 'sala_propria' ? t(`reuniao.${a.reuniao_tipo}`) : t('campos.linkReuniao')}</dt>
              <dd className="mt-1 break-all">
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-acento-forte hover:underline" title={t('detalhe.abrirLink')}>
                    {a.link_reuniao}
                  </a>
                ) : (
                  a.link_reuniao
                )}
              </dd>
            </div>
          )}
          {a.convidados.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="kicker">{t('campos.convidados')}</dt>
              <dd className="mt-1">
                <ul className="flex flex-col gap-1">
                  {a.convidados.map((c) => (
                    <li key={chaveConvidado(c)} className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 grow basis-24 break-words">{rotuloConvidado(c)}</span>
                      <span className="min-w-min">
                        <Selo>{t(`convidados.${c.tipo}`)}</Selo>
                      </span>
                    </li>
                  ))}
                </ul>
                {/* Reunião livre (03/11): com a conta pessoal o convite é do Google; sem ela, o e-mail do sistema. */}
                <p className="mt-2 text-xs text-tinta-3">{ehReuniaoLivre(a) ? t(a.convite_google && a.responsavel_usuario_id === s.usuario.id ? 'reuniaoLivre.convite.google' : 'reuniaoLivre.convite.email') : t('convidados.google')}</p>
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}

/** Drawer do agendamento: dados, campos personalizados, ações de estado (confirmar, realizado, falta, remarcar, cancelar) e chatter. */
/** Lote crm8 (item 5): `aoEditar` (Reuniões/Agenda do CRM) põe o "Editar reunião" (assunto, contatos e observações) nas ações. */
/** Rlote-10 (SPEC-M1): `reuniaoCrm` (Reuniões/Agenda do CRM) devolve as ações do antigo "Mais opções" da reunião encerrada. */
/** Reunião livre (03/11): `aoEditarLivre` é o "Editar reunião" dela fora do CRM; sem cliente nem serviço, o detalhe só mostra o que ela tem. */
export function DetalheAgendamento({ id, aoFechar, aoEditar, aoEditarLivre, reuniaoCrm }: { id: string | null; aoFechar: () => void; aoEditar?: (a: Agendamento) => void; aoEditarLivre?: (a: Agendamento) => void; reuniaoCrm?: boolean }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const nomeDoRecurso = useNomeDoRecurso();
  // Fase 2 (W25): depois de excluir, o detalhe não pede mais o agendamento (segue montado até fechar).
  const [excluido, setExcluido] = useState<string | null>(null);
  // Ficha 2026-11-03: enquanto o link do Meet não chega, relê a cada 5 s, com teto de 3 min por abertura; ao chegar, para.
  const esperaMeet = useRef<{ id: string; desde: number } | null>(null);
  const q = useQuery({
    queryKey: [CHAVE, 'agendamento', id],
    queryFn: () => get<Agendamento>(`/agenda/agendamentos/${id}`),
    enabled: !!id && excluido !== id,
    refetchInterval: (consulta) => {
      const d = consulta.state.data;
      if (!id || !d || d.reuniao_tipo !== 'google_meet' || d.link_reuniao || !ESTADOS_ABERTOS.includes(d.estado)) return false;
      if (esperaMeet.current?.id !== id) esperaMeet.current = { id, desde: Date.now() };
      return Date.now() - esperaMeet.current.desde < TETO_MEET_MS ? ESPERA_MEET_MS : false;
    },
  });
  useEffect(() => {
    if (!id) esperaMeet.current = null;
  }, [id]);
  // Rodada rrl-1 (SPEC-B7): passado o teto da espera, o texto para de prometer "em instantes" e diz onde conferir.
  const [meetDemorou, setMeetDemorou] = useState(false);
  const aguardandoMeet = !!q.data && q.data.reuniao_tipo === 'google_meet' && !q.data.link_reuniao && ESTADOS_ABERTOS.includes(q.data.estado);
  useEffect(() => {
    setMeetDemorou(false);
    if (!aguardandoMeet) return;
    const prazo = setTimeout(() => setMeetDemorou(true), TETO_MEET_MS);
    return () => clearTimeout(prazo);
  }, [id, aguardandoMeet]);
  const [cancelando, setCancelando] = useState(false);
  const [serie, setSerie] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [remarcando, setRemarcando] = useState(false);
  // Rodada rrl-4 (VIS-B2): o bloco some ao aplicar, e o foco volta ao botão que o abriu. No efeito de layout, junto com a saída do bloco:
  // a camada recupera o foco perdido no setTimeout seguinte e o levaria ao vizinho. Quem já seguiu para outro lugar fica onde está.
  const voltarAoRemarcar = useRef(false);
  useLayoutEffect(() => {
    if (!voltarAoRemarcar.current) return;
    voltarAoRemarcar.current = false;
    if (focoPerdido(document.activeElement, document.body)) document.querySelector<HTMLElement>('[data-botao-remarcar]')?.focus();
  }, [remarcando]);
  const [excluindo, setExcluindo] = useState(false);
  const [dia, setDia] = useState('');
  const [horario, setHorario] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  // Lista de 02/11, item 6 (rl0211-1 SPEC-M1): na reunião do CRM o "Criar tarefa" abre o formulário do CRM (o do botão flutuante),
  // com o parceiro da reunião; sem a criação publicada (sem permissão ou módulo), o botão some. Fora do CRM, nada muda.
  const criacaoTarefa = useCriacaoDaChave('tarefa');
  const criacao = useAbrirCriacao();

  const depois = async (msg?: string) => {
    await qc.invalidateQueries({ queryKey: [CHAVE] });
    await qc.invalidateQueries({ queryKey: ['chatter', 'agenda.agendamento', id] });
    toast.sucesso(msg ?? tn('comum.salvo'));
  };
  const falhou = (e: unknown) => setErro(e instanceof ErroRequisicao && e.status === 409 ? t('detalhe.conflito') : mensagemDeErro(e));
  const useAcao = (caminho: string, corpo?: unknown) =>
    useMutation({
      mutationFn: () => post(`/agenda/agendamentos/${id}/${caminho}`, corpo),
      onMutate: () => setErro(null),
      onSuccess: () => depois(),
      onError: falhou,
    });
  const confirmar = useAcao('confirmar');
  const realizar = useAcao('realizar');
  const faltou = useAcao('faltou');
  const reabrir = useAcao('reabrir');
  const cancelar = useMutation({
    mutationFn: () => post(`/agenda/agendamentos/${id}/cancelar`, { serie, motivo: motivo.trim() || null }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      setCancelando(false);
      setSerie(false);
      setMotivo('');
      await depois();
    },
    onError: (e) => (setCancelando(false), falhou(e)),
  });
  const remarcar = useMutation({
    mutationFn: () => post(`/agenda/agendamentos/${id}/remarcar`, { inicio: horario }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      voltarAoRemarcar.current = true;
      setRemarcando(false);
      setHorario('');
      await depois();
    },
    onError: async (e) => {
      falhou(e);
      setHorario('');
      await qc.invalidateQueries({ queryKey: [CHAVE, 'disponibilidade'] });
    },
  });

  const fechar = () => {
    setRemarcando(false);
    setCancelando(false);
    setExcluindo(false);
    setErro(null);
    aoFechar();
  };
  // Fase 2 (W25, K21): o agendamento cancelado sai da agenda para a lixeira (30 dias).
  const excluir = useMutation({
    mutationFn: async () => {
      await del(`/agenda/agendamentos/${id}`);
      setExcluido(id);
    },
    onMutate: () => setErro(null),
    onSuccess: async () => {
      setExcluindo(false);
      toast.sucesso(tn('exclusao.agendamento.feito'));
      aoFechar();
      await qc.invalidateQueries({ queryKey: [CHAVE] });
    },
    onError: (e) => (setExcluindo(false), falhou(e)),
  });

  return (
    <Drawer aberto={!!id} aoFechar={fechar} largura="lg" titulo={q.data ? (ehReuniaoLivre(q.data) ? textoLivre(q.data.assunto, q.data.parceiro_nome, ...(q.data.convidados ?? []).map((c) => c.nome)) || t('crm.semAssunto') : nomeParceiro(q.data.parceiro_nome)) : t('nome')}
      rodape={
        <Botao variante="fantasma" onClick={fechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <Estado consulta={q}>
        {(a) => {
          const aberto = ESTADOS_ABERTOS.includes(a.estado);
          const comecou = new Date(a.inicio) <= new Date();
          const podeEditar = s.pode('agenda.editar');
          // Val5 (29/10): falta, remarcar e cancelar pedem `agenda.cancelar`; confirmar e realizado seguem com `agenda.editar`.
          const podeCancelar = s.pode('agenda.cancelar');
          const livre = ehReuniaoLivre(a);
          const editar = livre ? (aoEditarLivre ?? aoEditar) : aoEditar;
          const dias = a.dia_inteiro ? diasDoDiaInteiro(a, s.workspace.fuso) : null;
          return (
            <div className="flex flex-col gap-5">
              {erro && (
                <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                  {erro}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Selo cor={COR_ESTADO[a.estado]}>{t(`estados.${a.estado}`)}</Selo>
                {a.encaixe && <Selo cor="laranja">{t('campos.encaixe')}</Selo>}
                {a.google_event_id && <Selo cor="ciano">{t('detalhe.googleOk')}</Selo>}
                {a.serie_id && a.recorrencia && <Selo>{t('detalhe.serie', { frequencia: t(`recorrencia.${a.recorrencia}`).toLowerCase(), ate: fmtData(a.recorrencia_ate) })}</Selo>}
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                {a.parceiro_id && (
                  <div>
                    <dt className="kicker">{t('campos.cliente')}</dt>
                    <dd className="mt-1 break-words">
                      {/* W20: cliente de uma empresa que a pessoa não vê: o rótulo neutro, sem o link (a ficha seria 404). */}
                      <LinkParceiro id={a.parceiro_id} nome={a.parceiro_nome} className="font-medium hover:text-acento-forte" />
                      {a.parceiro_telefone && <span className="numero ml-2 text-tinta-3">{telefoneFormatado(a.parceiro_telefone)}</span>}
                    </dd>
                  </div>
                )}
                {!livre && (
                  <div>
                    <dt className="kicker">{t('campos.servico')}</dt>
                    <dd className="mt-1 break-words">
                      {nomeCadastro(a.servico_nome)} <span className="text-tinta-3">({a.duracao_min} min)</span>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="kicker">{livre ? t('crm.responsavel') : t('campos.recurso')}</dt>
                  <dd className="mt-1 break-words">{nomeDoRecurso(a.recurso_id, a.recurso_nome)}</dd>
                </div>
                <div>
                  <dt className="kicker">{t('campos.horario')}</dt>
                  <dd className="numero mt-1" data-horario-detalhe="">
                    {dias ? (dias.diaFinal === dias.dia ? t('reuniaoLivre.diaInteiroEm', { data: fmtData(dias.dia) }) : t('reuniaoLivre.diaInteiroDe', { de: fmtData(dias.dia), ate: fmtData(dias.diaFinal) })) : tn('comum.intervalo', { de: dataHora(a.inicio), ate: hora(a.fim) })}
                  </dd>
                </div>
                {!livre && (
                  <div>
                    <dt className="kicker">{t('detalhe.preco')}</dt>
                    <dd className="numero mt-1">{dinheiro(a.preco_centavos)}</dd>
                  </div>
                )}
                {a.lembretes_enviados.length > 0 && (
                  <div>
                    <dt className="kicker">{t('detalhe.lembretes')}</dt>
                    <dd className="numero mt-1">{a.lembretes_enviados.join(', ')}</dd>
                  </div>
                )}
                {a.observacoes && (
                  <div className="sm:col-span-2">
                    <dt className="kicker">{livre ? t('reuniaoLivre.descricao') : t('campos.observacoes')}</dt>
                    <dd className="mt-1 whitespace-pre-wrap break-words">{textoLivre(a.observacoes, a.parceiro_nome, ...(a.convidados ?? []).map((c) => c.nome))}</dd>
                  </div>
                )}
              </dl>

              {/* Reunião livre: local, Meet e convidados mudam no "Editar reunião"; campos personalizados são do agendamento com serviço. */}
              <DetalhesDoAgendamento key={`det-${a.id}`} a={a} podeEditar={podeEditar && !(livre && editar && (aberto || reuniaoCrm))} aoSalvar={() => depois()} meetDemorou={meetDemorou} />

              {!livre && <ExtrasDoAgendamento key={a.id} a={a} podeEditar={podeEditar} aoSalvar={() => depois()} />}

              {/* Gravação (20/10): a pasta da reunião gravada na sala própria. */}
              {a.reuniao_tipo === 'sala_propria' && <Gravacoes key={`grav-${a.id}`} agendamentoId={a.id} />}

              {(podeEditar || podeCancelar) && aberto && (
                <div className="flex flex-wrap gap-2">
                  {podeEditar && a.estado === 'marcado' && (
                    <Botao variante="primario" carregando={confirmar.isPending} onClick={() => confirmar.mutate()}>
                      {t('acoes.confirmar')}
                    </Botao>
                  )}
                  {comecou && (
                    <>
                      {podeEditar && (
                        <Botao carregando={realizar.isPending} onClick={() => realizar.mutate()}>
                          {t('acoes.realizado')}
                        </Botao>
                      )}
                      {podeCancelar && (
                        <Botao carregando={faltou.isPending} onClick={() => faltou.mutate()}>
                          {t('acoes.faltou')}
                        </Botao>
                      )}
                    </>
                  )}
                  {podeCancelar && (
                  <Botao
                    data-botao-remarcar=""
                    onClick={() => {
                      setRemarcando((x) => !x);
                      setDia(diaLocal(new Date(a.inicio)));
                      setHorario('');
                    }}
                  >
                    {t('acoes.remarcar')}
                  </Botao>
                  )}
                  {editar && podeEditar && (
                    <Botao onClick={() => editar(a)} data-editar-reuniao="">
                      {t('crm.editar')}
                    </Botao>
                  )}
                  {podeCancelar && (
                    <Botao variante="perigo" onClick={() => setCancelando(true)}>
                      {t('acoes.cancelar')}
                    </Botao>
                  )}
                </div>
              )}
              {/* Rlote-10 (SPEC-M1): na reunião do CRM realizada ou não ocorrida voltam as ações da lista antiga (decisão 9 de 27/10):
                  trocar realizada <-> não ocorrida, voltar para agendada e editar; permissões do val5 (editar e realizada com
                  agenda.editar; não ocorrida e voltar com agenda.cancelar). */}
              {reuniaoCrm && (a.estado === 'realizado' || a.estado === 'faltou') && (podeEditar || podeCancelar) && (
                <div className="flex flex-wrap gap-2" data-acoes-reuniao-encerrada="">
                  {podeEditar && acoesDaReuniao(a.estado, a.inicio).realizar && (
                    <Botao carregando={realizar.isPending} onClick={() => realizar.mutate()} data-acao-reuniao="realizar">
                      {t('crm.acoes.realizada')}
                    </Botao>
                  )}
                  {podeCancelar && acoesDaReuniao(a.estado, a.inicio).faltou && (
                    <Botao carregando={faltou.isPending} onClick={() => faltou.mutate()} data-acao-reuniao="faltou">
                      {t('crm.acoes.naoOcorrida')}
                    </Botao>
                  )}
                  {podeCancelar && (
                    <Botao carregando={reabrir.isPending} onClick={() => reabrir.mutate()} data-acao-reuniao="reabrir">
                      {t('crm.acoes.voltarAgendada')}
                    </Botao>
                  )}
                  {editar && podeEditar && (
                    <Botao onClick={() => editar(a)} data-editar-reuniao="">
                      {t('crm.editar')}
                    </Botao>
                  )}
                </div>
              )}
              {/* Rlote-11 (SPEC-B2): o aviso de encerrado só quando não há ação nenhuma (a reunião do CRM encerrada tem as do bloco acima). */}
              {!aberto && !(reuniaoCrm && (a.estado === 'realizado' || a.estado === 'faltou') && (podeEditar || podeCancelar)) && <p className="text-sm text-tinta-3">{t('detalhe.semAcoes')}</p>}
              {a.estado === 'cancelado' && s.pode('agenda.excluir') && (
                <div className="flex flex-wrap gap-2">
                  <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
                    {tn('exclusao.agendamento.botao')}
                  </Botao>
                </div>
              )}
              <Confirmar
                aberto={excluindo}
                aoFechar={() => setExcluindo(false)}
                aoConfirmar={() => excluir.mutate()}
                carregando={excluir.isPending}
                perigoso
                titulo={tn('exclusao.agendamento.titulo')}
                texto={tn('exclusao.agendamento.texto')}
              />

              {remarcando && livre && (
                <RemarcarReuniaoLivre
                  key={a.id}
                  a={a}
                  aoFecharSemMudar={() => {
                    voltarAoRemarcar.current = true;
                    setRemarcando(false);
                  }}
                  aoRemarcar={async () => {
                    voltarAoRemarcar.current = true;
                    setRemarcando(false);
                    await qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
                    await depois();
                  }}
                />
              )}
              {remarcando && !livre && a.servico_id && (
                <section className="flex flex-col gap-3 rounded-xl border border-borda bg-sup-2 p-4">
                  <h3 className="text-sm font-semibold">{t('detalhe.remarcarTitulo')}</h3>
                  <div className="max-w-52">
                    <Rotulo id="rem-dia" rotulo={t('campos.data')}>
                      <CampoData id="rem-dia" type="date" value={dia} onChange={(e) => (setDia(e.target.value), setHorario(''))} />
                    </Rotulo>
                  </div>
                  <SeletorHorario servicoId={a.servico_id} recursoId={a.recurso_id} dia={dia} valor={horario} aoEscolher={setHorario} />
                  <div>
                    <Botao variante="primario" disabled={!horario} carregando={remarcar.isPending} onClick={() => remarcar.mutate()}>
                      {t('acoes.aplicarRemarcacao')}
                    </Botao>
                  </div>
                </section>
              )}

              <Confirmar
                aberto={cancelando}
                aoFechar={() => setCancelando(false)}
                aoConfirmar={() => cancelar.mutate()}
                carregando={cancelar.isPending}
                perigoso
                titulo={t('detalhe.confirmarCancelar')}
                texto={
                  <div className="flex flex-col gap-3">
                    <p>{t('detalhe.textoCancelar')}</p>
                    <Rotulo id="can-motivo" rotulo={t('detalhe.motivo')}>
                      <EntradaSensivel mascara={null} livre id="can-motivo" value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
                    </Rotulo>
                    {a.serie_id && (
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={serie} onChange={(e) => setSerie(e.target.checked)} />
                        {t('detalhe.cancelarSerie')}
                      </label>
                    )}
                  </div>
                }
              />

              <div className="border-t border-borda pt-4">
                {reuniaoCrm && <DialogoCriacao aberta={criacao.aberta} aoFechar={criacao.fechar} contexto={{ ...(a.parceiro_id ? { contato: { id: a.parceiro_id, nome: a.parceiro_nome ?? '' } } : {}), ...(a.oportunidade_id ? { negociacao: { id: a.oportunidade_id } } : {}) }} />}
                <Chatter {...(reuniaoCrm ? { criarTarefa: criacaoTarefa ? () => criacao.abrir(criacaoTarefa) : null } : {})} entidade="agenda.agendamento" registroId={a.id} nomes={[a.parceiro_nome, ...(a.convidados ?? []).map((c) => c.nome)]} rotulosCampos={{ estado: t('campos.estado'), inicio: t('campos.inicio'), fim: t('campos.fim'), recurso_id: t('campos.recurso'), observacoes: livre ? t('reuniaoLivre.descricao') : t('campos.observacoes'), extras: tn('campos.personalizados'), local: t('campos.local'), link_reuniao: t('campos.linkReuniao'), reuniao_tipo: t('reuniao.campo'), convidados: t('campos.convidados'), encaixe: t('campos.encaixe'), assunto: t('crm.campos.assunto'), dia_inteiro: t('reuniaoLivre.diaInteiro') }} />
              </div>
            </div>
          );
        }}
      </Estado>
    </Drawer>
  );
}
